/* @flow */
import type { Axios } from 'axios';

import RestRequest from './request';
import type { RequestDataType, RequestParamsType } from './request';

type RestResourceType = {
    relations: Function,
};

type ResourceRelations = {
    [string]: RestResourceType,
};

export default function RestResource(
    api: Axios,
    endpoint: string,
    defaultRelations?: ResourceRelations,
    raw: boolean = false,
    preventDuplicates:boolean = false
) {
    const request = RestRequest(endpoint);
    request.setRaw(raw);
    request.setPreventDuplicates(preventDuplicates);

    const resource = {
        relations(): ResourceRelations {
            return {};
        },

        setRelations(relations: ResourceRelations) {
            resource.relations = () => relations;
            Object.keys(relations).forEach((key) => {
                Object.defineProperty(resource, key, {
                    get() {
                        return RestResource(
                            api,
                            `${request.getRoute()}/${key}`,
                            relations[key].relations()
                        );
                    },
                });
            });
        },

        getUrl(params: RequestParamsType = {}): string {
            return request.setParams(params).getUrl();
        },

        getWith(embed: Array<string>, params: RequestParamsType = {}) {
            const paramsWithEmbed = {
                embed: embed.join(','),
                ...params,
            };
            return this.get(paramsWithEmbed);
        },

        get(params: RequestParamsType = {}) {
            return request.setMethod('get').setParams(params).execute(api);
        },

        put(params: RequestParamsType = {}) {
            return request.setMethod('put').setParams(params).execute(api);
        },

        patch(params: RequestParamsType = {}) {
            return request.setMethod('patch').setParams(params).execute(api);
        },

        post(data: RequestDataType, params: RequestParamsType = {}) {
            return request
                .setData(data)
                .setParams(params)
                .setMethod('post')
                .execute(api);
        },

        create(data: RequestDataType, params: RequestParamsType = {}) {
            return request
                .setData(data)
                .setParams(params)
                .setMethod('post')
                .execute(api);
        },

        update(data: RequestDataType, params: RequestParamsType = {}) {
            return request
                .setData(data)
                .setParams(params)
                .setMethod('put')
                .execute(api);
        },

        delete(params: RequestParamsType = {}) {
            return request
                .setParams(params)
                .setMethod('delete')
                .execute(api);
        },

        find(id: number) {
            return RestResource(
                api,
                `${request.getRoute()}/${id}`,
                resource.relations()
            );
        },

        all(params: RequestParamsType = {}) {
            return request.setParams(params).execute(api);
        },

        allWith(embed: Array<string>, params: RequestParamsType = {}) {
            return this.getWith(embed, params);
        },

        exists(params: RequestParamsType = {}) {
            return request
                .setParams(params)
                .setMethod('head')
                .execute(api);
        },

        segment(method: string) {
            return RestResource(
                api,
                `${request.getRoute()}/${method}`,
                resource.relations()
            );
        },

        raw() {
            return RestResource(
                api,
                request.getRoute(),
                resource.relations(),
                true
            );
        },
    };

    if (defaultRelations) {
        resource.setRelations(defaultRelations);
    }

    return resource;
}
